import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { z } from "zod"

import type { CompanyProductAssociationListItem } from "../../hooks/useGetCompanyProductAssociations"
import { useUpdateCompanyProductAssociation } from "../../hooks/useUpdateCompanyProductAssociation"

import { AssociateProductCustomerInfoStep } from "./AssociateProductCustomerInfoStep"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { Product } from "@/graphql/codegen/graphql"

const editAssociationFormSchema = z.object({
  customerCode: z.array(z.string()).optional(),
  customerDescription: z.string().optional(),
})

type EditAssociationFormValues = z.infer<typeof editAssociationFormSchema>

interface EditProductAssociationDialogProps {
  association: CompanyProductAssociationListItem
  onClose: (shouldRefetch?: boolean) => void
}

export const EditProductAssociationDialog: FC<EditProductAssociationDialogProps> = ({ association, onClose }) => {
  const { updateAssociation, isUpdatingAssociation } = useUpdateCompanyProductAssociation()

  const methods = useForm<EditAssociationFormValues>({
    resolver: zodResolver(editAssociationFormSchema),
    defaultValues: {
      customerCode: association.customerCode ?? [],
      customerDescription: association.customerDescription ?? "",
    },
  })

  const {
    handleSubmit,
    formState: { isDirty },
  } = methods

  const canSubmit = isDirty && !isUpdatingAssociation

  const onSubmit = handleSubmit(async (formValues: EditAssociationFormValues) => {
    const success = await updateAssociation({
      id: association.companyProductAssociationId,
      customerCode: formValues.customerCode?.length ? formValues.customerCode : undefined,
      customerDescription: formValues.customerDescription || undefined,
    })
    if (success) onClose(true)
  })

  return (
    <Dialog
      open
      onClose={() => onClose()}
      fullWidth
      sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { maxWidth: "1072px", width: "100%" } } }}
      data-testid='edit-product-association-dialog'
    >
      <DialogHeader title='Edit Product Association' onClose={() => onClose()} />

      <FormProvider {...methods}>
        <DialogContent classes={{ root: "p-10" }}>
          {association.product && (
            <AssociateProductCustomerInfoStep
              product={association.product as unknown as Product}
              sectionTwoTitle="2. Edit Customer's Default Product Info"
            />
          )}
        </DialogContent>
      </FormProvider>

      <DialogActions className='flex justify-between p-10'>
        <Button appearance='outlined' onClick={() => onClose()} data-testid='edit-association-cancel-button'>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!canSubmit}
          loading={isUpdatingAssociation}
          data-testid='edit-association-save-button'
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
